import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { BiMaleFemale } from 'react-icons/bi';

// ChartJS bileşenlerini kaydet
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

const GenderDoughnutChart = ({ data }) => {
  const chartData = useMemo(() => {
    // Labels ve counts içeren eski format kontrolü
    if (data && !Array.isArray(data) && data.labels && data.counts) {
      // Cinsiyet verisini kontrol et ve doğru formata dönüştür
      const labels = data.labels;
      const counts = data.counts;
      
      const femaleIndex = labels.findIndex(label => 
        label.toLowerCase().includes('kız') || label.toLowerCase().includes('k'));
      const maleIndex = labels.findIndex(label => 
        label.toLowerCase().includes('erkek') || label.toLowerCase().includes('e'));
      
      const female = femaleIndex !== -1 ? counts[femaleIndex] : 0;
      const male = maleIndex !== -1 ? counts[maleIndex] : 0;
      
      return {
        labels: ['Erkek', 'Kız'],
        datasets: [
          {
            data: [male, female],
            backgroundColor: [
              'rgba(59, 130, 246, 0.8)', // Mavi (Erkek)
              'rgba(236, 72, 153, 0.8)', // Pembe (Kız)
            ],
            borderColor: [
              'rgba(59, 130, 246, 1)',
              'rgba(236, 72, 153, 1)',
            ],
            borderWidth: 2,
            hoverBackgroundColor: [
              'rgba(59, 130, 246, 0.9)',
              'rgba(236, 72, 153, 0.9)',
            ],
            hoverBorderColor: [
              'rgba(59, 130, 246, 1)',
              'rgba(236, 72, 153, 1)',
            ],
            hoverBorderWidth: 3,
          },
        ],
      };
    }

    // Standart erkek/kız alanları içeren obje formatı kontrolü
    if (data && !Array.isArray(data)) {
      // null, undefined kontrolü
      const male = data.male !== undefined && data.male !== null ? Number(data.male) : 0;
      const female = data.female !== undefined && data.female !== null ? Number(data.female) : 0;
      
      if (isNaN(male) || isNaN(female)) {
        return {
          labels: ['Veri Yok'],
          datasets: [
            {
              data: [1],
              backgroundColor: ['rgba(209, 213, 219, 0.5)'],
              borderColor: ['rgba(209, 213, 219, 1)'],
              borderWidth: 1,
            },
          ],
        };
      }
      
      return {
        labels: ['Erkek', 'Kız'],
        datasets: [
          {
            data: [male, female],
            backgroundColor: [
              'rgba(59, 130, 246, 0.8)', // Mavi (Erkek)
              'rgba(236, 72, 153, 0.8)', // Pembe (Kız)
            ],
            borderColor: [
              'rgba(59, 130, 246, 1)',
              'rgba(236, 72, 153, 1)',
            ],
            borderWidth: 2,
            hoverBackgroundColor: [
              'rgba(59, 130, 246, 0.9)',
              'rgba(236, 72, 153, 0.9)',
            ],
            hoverBorderColor: [
              'rgba(59, 130, 246, 1)',
              'rgba(236, 72, 153, 1)',
            ],
            hoverBorderWidth: 3,
          },
        ],
      };
    }

    // Standart eksik veri durumu
    return {
      labels: ['Veri Yok'],
      datasets: [
        {
          data: [1],
          backgroundColor: ['rgba(209, 213, 219, 0.5)'],
          borderColor: ['rgba(209, 213, 219, 1)'],
          borderWidth: 1,
        },
      ],
    };
  }, [data]);

  const options = useMemo(() => {
    // Toplam öğrenci sayısını hesapla
    let total = 0;
    
    if (data) {
      if (!Array.isArray(data) && data.counts) {
        // Eski format için
        total = data.counts.reduce((sum, count) => sum + count, 0);
      } else if (!Array.isArray(data) && data.male !== undefined && data.female !== undefined) {
        // Standart format için
        const male = Number(data.male) || 0;
        const female = Number(data.female) || 0;
        total = male + female;
      }
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index'
      },
      animation: {
        animateRotate: true,
        animateScale: true,
        duration: 1000,
        easing: 'easeOutQuart'
      },
      hover: {
        animationDuration: 300
      },
      plugins: {
        legend: {
          position: 'bottom',
          align: 'center',
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 12,
            padding: 20,
            font: {
              size: 12,
              weight: 'bold'
            },
            generateLabels: function(chart) {
              const data = chart.data;
              if (data.labels.length && data.datasets.length) {
                return data.labels.map((label, i) => {
                  const value = data.datasets[0].data[i];
                  const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                  return {
                    text: `${label}: ${value} (${percentage}%)`,
                    fillStyle: data.datasets[0].backgroundColor[i],
                    strokeStyle: data.datasets[0].borderColor[i],
                    lineWidth: 2,
                    pointStyle: 'circle',
                    index: i
                  };
                });
              }
              return [];
            }
          }
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          titleColor: 'rgba(255, 255, 255, 1)',
          bodyColor: 'rgba(255, 255, 255, 1)',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          borderWidth: 1,
          padding: 15,
          cornerRadius: 8,
          displayColors: true,
          callbacks: {
            title: function(context) {
              return 'Cinsiyet Dağılımı';
            },
            label: function(context) {
              const value = context.raw || 0;
              const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
              return `${context.label}: ${value} öğrenci (${percentage}%)`;
            },
            afterLabel: function(context) {
              return `Toplam: ${total} öğrenci`;
            }
          }
        }
      },
      cutout: '70%',
      radius: '90%',
      elements: {
        arc: {
          borderWidth: 2,
          hoverBorderWidth: 3
        }
      }
    };
  }, [data]);

  // Toplam sayı için
  const totalCount = useMemo(() => {
    if (!data) return 0;
    
    if (!Array.isArray(data) && data.counts) {
      // Eski format için
      return data.counts.reduce((sum, count) => sum + count, 0);
    } else if (!Array.isArray(data) && data.male !== undefined && data.female !== undefined) {
      // Standart format için
      const male = Number(data.male) || 0;
      const female = Number(data.female) || 0;
      return male + female;
    }
    
    return 0;
  }, [data]);

  const hasValidData = useMemo(() => {
    if (!data) return false;
    
    if (!Array.isArray(data) && data.counts && data.labels) {
      return data.labels.length > 0 && data.counts.length > 0;
    } else if (!Array.isArray(data)) {
      const male = Number(data.male);
      const female = Number(data.female);
      return !isNaN(male) || !isNaN(female);
    }
    
    return false;
  }, [data]);

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 h-full">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-500" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
        Cinsiyet Dağılımı
      </h3>
      <div className="flex flex-col items-center justify-center h-full">
        {!hasValidData ? (
          <div className="text-center text-gray-500">
            <p>Cinsiyet dağılımı verisi bulunamadı</p>
          </div>
        ) : (
          <div className="relative w-full max-w-[280px] h-[280px] flex items-center justify-center">
            {/* Toplam sayı - chart'ın altında */}
            <div className="absolute inset-0 flex items-center justify-center z-0">
              <div className="text-center">
                <div className="text-4xl font-bold text-gray-800">{totalCount}</div>
                <div className="text-sm text-gray-500 font-medium">Toplam Öğrenci</div>
              </div>
            </div>
            {/* Chart - toplam sayının üzerinde */}
            <div className="relative w-full h-full z-10">
              <Doughnut data={chartData} options={options} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenderDoughnutChart;